import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Table, Button } from 'antd';
import type { TableColumnsType } from 'antd';
import EditableDrawer from 'components/EditableDrawer';
import { useQuery, useLazyQuery } from '@apollo/client';
import { TmsParcelDetailInfoType } from '../graphql/queries/getTmsParcelDetailsByBagIds';
import { GET_LOADING_ORDER_DETAIL_WAYPOINT_COUNT_BY_TASK_ID_AND_WAYPOINT_ID_FOR_PORTAL, GetLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortalResponse, GetLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortalVariables } from '../graphql/queries/getLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortal';
import { GET_LOADING_ORDER_DETAILS_BY_WAYPOINT_ID, LoadingOrderDetailInfoType, GetLoadingOrderDetailsByWaypointIdResponse, GetLoadingOrderDetailsByWaypointIdVariables } from '../graphql/queries/getLoadingOrderDetailsByWaypointId';
import { TmsTaskWaypointType } from '../graphql/queries/getTmsTaskDetail';
import dayjs from 'dayjs';
import ParcelDetailDrawer from 'components/ParcelDetailDrawer';
import { getUserRole } from 'utils/auth';
import InputFilterItem from 'components/InputFilterItem';
import SingleSelect from 'components/SingleSelect';
import { overallStatusOptions } from 'config/filterOptions';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { t } from '../../../../i18n';

interface LoadingDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    waypointId: number; // 这里应该是taskWaypointId，不是waypointId
    taskId?: number; // 任务ID，用于查询包裹和包牌数量
    taskNumber?: string;
    sortingCode?: string;
    loadingTime?: string;
    routeName?: string;
    taskWaypoints?: TmsTaskWaypointType[]; // 任务途经点数据（tms_task_waypoint表），用于构建目的站点选项
    waypointIdToSortingCodeMap?: Map<number, string>; // waypointId到sortingCode的映射，用于显示
}

const LoadingDetailDrawer: React.FC<LoadingDetailDrawerProps> = ({
    open,
    onClose,
    waypointId,
    taskId,
    taskNumber,
    sortingCode: sortingCodeProp,
    loadingTime: loadingTimeProp,
    routeName,
    taskWaypoints = [],
    waypointIdToSortingCodeMap
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    const userRole = getUserRole();

    // 筛选条件类型
    interface FilterConditions {
        trackingNumber: string;
        status: string;
        site: string;
    }

    // 临时筛选条件（用于输入，不会触发查询）
    const [tempFilters, setTempFilters] = useState<FilterConditions>({
        trackingNumber: '',
        status: '',
        site: ''
    });

    // 实际应用的筛选条件（用于查询）
    const [appliedFilters, setAppliedFilters] = useState<FilterConditions>({
        trackingNumber: '',
        status: '',
        site: ''
    });

    // 当 drawer 打开时重置分页和筛选，关闭时也重置
    useEffect(() => {
        const emptyFilters: FilterConditions = {
            trackingNumber: '',
            status: '',
            site: ''
        };
        if (open) {
            // drawer 打开时重置状态
            setCurrentPage(1);
            setPageSize(10);
            setTempFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
        } else {
            // drawer 关闭时也重置状态（双重保险）
            setCurrentPage(1);
            setPageSize(10);
            setTempFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
        }
    }, [open]);

    // 查询当前途径点的包裹和包牌数量
    const { data: waypointCountData, loading: waypointCountLoading } = useQuery<GetLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortalResponse, GetLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortalVariables>(
        GET_LOADING_ORDER_DETAIL_WAYPOINT_COUNT_BY_TASK_ID_AND_WAYPOINT_ID_FOR_PORTAL,
        {
            variables: {
                taskId: taskId || 0,
                waypointId: waypointId
            },
            skip: !taskId || !waypointId || !open
        }
    );

    // 判断是否有 overallStatus 筛选条件
    const hasStatusFilter = useMemo(() => {
        return appliedFilters.status && appliedFilters.status.trim() !== '';
    }, [appliedFilters.status]);

    // ===== 统一使用新接口（支持 overallStatus 参数） =====
    const [executeNewQuery, { data: newParcelData, loading: newLoading }] = useLazyQuery<GetLoadingOrderDetailsByWaypointIdResponse, GetLoadingOrderDetailsByWaypointIdVariables>(
        GET_LOADING_ORDER_DETAILS_BY_WAYPOINT_ID,
        {
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true
        }
    );

    // 跟踪当前查询的 waypointId，用于判断数据是否匹配
    const [currentQueryWaypointId, setCurrentQueryWaypointId] = useState<number | null>(null);

    // 当 waypointId 改变时，清空所有状态
    useEffect(() => {
        if (open && waypointId) {
            // 清空所有状态
            const emptyFilters: FilterConditions = {
                trackingNumber: '',
                status: '',
                site: ''
            };
            setCurrentPage(1);
            setPageSize(10);
            setTempFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
            setParcelDetailVisible(false);
            setSelectedOrderId('');
            setTotalParcelCount(0);
            setCurrentQueryWaypointId(null); // 先清空，等待新数据
        }
    }, [waypointId, open]);

    // 统一使用新接口（支持 overallStatus 参数）
    useEffect(() => {
        if (!open || !waypointId) {
            setCurrentQueryWaypointId(null);
            return;
        }

        // 统一使用新接口，根据是否有 overallStatus 筛选决定是否传递该参数
        executeNewQuery({
            variables: {
                waypointId: waypointId,
                pageRequest: {
                    page: currentPage,
                    size: pageSize
                },
                trackingNumber: appliedFilters.trackingNumber?.trim() || undefined,
                sortingCode: appliedFilters.site?.trim() || undefined,
                overallStatus: hasStatusFilter ? appliedFilters.status?.trim() || undefined : undefined
            }
        }).then(() => {
            setCurrentQueryWaypointId(waypointId);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, waypointId, hasStatusFilter, appliedFilters, currentPage, pageSize]);

    // 使用新接口的 loading 状态
    const loading = newLoading;

    // 应用筛选条件（将临时筛选条件应用到实际筛选条件，触发查询）
    const handleApplyFilter = useCallback(() => {
        setAppliedFilters({
            trackingNumber: tempFilters.trackingNumber.trim(),
            status: tempFilters.status.trim(),
            site: tempFilters.site.trim()
        });
        setCurrentPage(1); // 重置到第一页
        // 查询会在 useEffect 中自动触发，因为 appliedFilters 已经更新
    }, [tempFilters]);

    // 重置筛选条件
    const handleResetFilter = useCallback(() => {
        const emptyFilters: FilterConditions = {
            trackingNumber: '',
            status: '',
            site: ''
        };
        setTempFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setCurrentPage(1); // 重置到第一页
        // 查询会在 useEffect 中自动触发，因为 appliedFilters 已经清空
    }, []);

    // 构建站点选项（从 task waypoints 中提取，value和label都是sortingCode）
    const siteOptions = useMemo(() => {
        const siteSet = new Set<string>();
        taskWaypoints.forEach(wp => {
            const sortingCode = waypointIdToSortingCodeMap?.get(wp.waypointId);
            if (sortingCode) {
                siteSet.add(sortingCode);
            }
        });
        return Array.from(siteSet).sort().map(site => ({
            label: site,
            value: site // value 和 label 都是 sortingCode
        }));
    }, [taskWaypoints, waypointIdToSortingCodeMap]);

    // 使用新接口的数据
    const paginatedData = useMemo(() => {
        if (currentQueryWaypointId !== waypointId) {
            return [];
        }
        return newParcelData?.getLoadingOrderDetailsByWaypointId?.content || [];
    }, [currentQueryWaypointId, waypointId, newParcelData]);

    // 获取当前的 totalElements
    const currentTotalElements = useMemo(() => {
        return newParcelData?.getLoadingOrderDetailsByWaypointId?.totalElements || 0;
    }, [newParcelData]);

    // 保存总数（不受筛选影响）
    const [totalParcelCount, setTotalParcelCount] = useState<number>(0);

    // 当有新数据且没有筛选条件时，更新总数
    useEffect(() => {
        if (currentQueryWaypointId !== waypointId) {
            return;
        }

        const hasFilters = (appliedFilters.trackingNumber && appliedFilters.trackingNumber.trim()) ||
                           (appliedFilters.status && appliedFilters.status.trim()) ||
                           (appliedFilters.site && appliedFilters.site.trim());

        // 只有在没有筛选条件时，才更新总数
        if (!hasFilters && currentTotalElements > 0) {
            setTotalParcelCount(currentTotalElements);
        }
    }, [currentTotalElements, appliedFilters, currentQueryWaypointId, waypointId]);

    // 当 drawer 关闭时重置总数
    useEffect(() => {
        if (!open) {
            setTotalParcelCount(0);
        }
    }, [open]);

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    // 当筛选条件改变时，重置到第一页
    useEffect(() => {
        setCurrentPage(1);
    }, [appliedFilters]);

    // 使用传入的sortingCode，如果没有则使用'-'
    const sortingCode = sortingCodeProp || '-';

    // 格式化时间
    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return '-';
        return dayjs(timeStr).format('YYYY-MM-DD HH:mm:ss');
    };
    
    // 使用接口返回的包裹和包牌数量，如果没有则使用原来的计算方式作为后备
    const parcelCount = waypointCountData?.getLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortal?.parcelCount ?? totalParcelCount;
    const bagCount = waypointCountData?.getLoadingOrderDetailWaypointCountByTaskIdAndWaypointIdForPortal?.bagCount ?? 0;

    // 定义统一的数据类型（兼容两种接口返回）
    type ParcelDataItem = TmsParcelDetailInfoType | LoadingOrderDetailInfoType;

    // 处理点击tracking number
    const handleTrackingNumberClick = (parcel: ParcelDataItem) => {
        if (parcel.id) {
            setSelectedOrderId(parcel.id.toString());
            setParcelDetailVisible(true);
        }
    };

    const columns: TableColumnsType<ParcelDataItem> = useMemo(() => [
        {
            title: String(t('tms.task.loadingDetailDrawer.index')),
            key: 'index',
            align: 'left',
            width: 80,
            render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
        },
        {
            title: String(t('tms.task.loadingDetailDrawer.trackingNumber')),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left',
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => handleTrackingNumberClick(record)}
                    className="text-purple-600 hover:text-purple-800 p-0 h-auto"
                >
                    {text}
                </Button>
            ),
        },
        {
            title: String(t('tms.task.loadingDetailDrawer.status')),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'left',
            render: (status: string | null | undefined) => {
                if (!status) return '-';
                return String(t(`enum.overallStatus.${status}`));
            },
        },
        {
            title: String(t('tms.task.loadingDetailDrawer.destinationSite')),
            dataIndex: 'sortingCode',
            key: 'sortingCode',
            align: 'left',
            render: (value: string | null | undefined) => {
                if (!value) return '-';
                
                // 取前两段，例如 "LA-LADC-G074" -> "LA-LADC"
                const parts = value.split('-');
                if (parts.length < 2) return value;
                const secondPart = parts[1]; // LADC
                const firstPart = parts[0]; // LA
                
                // 如果路线名称包含第二段（LADC），显示第二段
                if (routeName && secondPart && routeName.includes(secondPart)) {
                    return firstPart + '-' + secondPart;
                }
                
                // 如果路线名称包含第一段（LA），显示第一段
                if (routeName && firstPart && routeName.includes(firstPart)) {
                    return firstPart;
                }
                
                // 否则显示 '-'
                return '-';
            },
        },
        {
            title: String(t('tms.task.loadingDetailDrawer.bagNumber')),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
            align: 'left',
            render: (value) => value || '-',
        },
        {
            title: String(t('tms.task.loadingDetailDrawer.bagTaskNumber')),
            dataIndex: 'bagTaskNumber',
            key: 'bagTaskNumber',
            align: 'left',
            width: 180,
            render: (value) => value || '-',
        },
    ], [currentPage, pageSize, routeName, t]);

    return (
        <EditableDrawer
            title={String(t('tms.task.loadingDetailDrawer.title'))}
            visible={open}
            onClose={onClose}
            width={1000}
            showDefaultFooter={false}
            className="[&_.ant-drawer-body>div]:overflow-visible"
            zIndex={1100}
        >
            <div className="mb-4 leading-6">
                <div className="mb-2">
                    <span className="mr-16">{String(t('tms.task.loadingDetailDrawer.taskNumber'))} <span className="font-bold">{taskNumber || '-'}</span></span>
                    <span>{String(t('tms.task.loadingDetailDrawer.site'))} <span className="font-bold">{sortingCode}</span></span>
                </div>
                <div className="mb-2">
                    <span>{String(t('tms.task.loadingDetailDrawer.loadingStartTime'))} <span className="font-bold">{formatTime(loadingTimeProp || null)}</span></span>
                </div>
                <div>
                    <span className="mr-16">{String(t('tms.task.loadingDetailDrawer.parcelCount'))} <span className="font-bold">{waypointCountLoading ? '-' : parcelCount}</span></span>
                    <span>{String(t('tms.task.loadingDetailDrawer.bagCount'))} <span className="font-bold">{waypointCountLoading ? '-' : bagCount}</span></span>
                </div>
            </div>

            {/* 筛选区域 */}
            <div className="mb-4 p-4 bg-gray-50 rounded">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <InputFilterItem
                            label={String(t('tms.task.loadingDetailDrawer.parcelNumber'))}
                            value={tempFilters.trackingNumber}
                            onChange={(e) => setTempFilters(prev => ({ ...prev, trackingNumber: e.target.value }))}
                            placeholder={String(t('tms.task.loadingDetailDrawer.enterParcelNumber'))}
                            forceHorizontal={true}
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <SingleSelect
                            label={String(t('tms.task.loadingDetailDrawer.parcelStatus'))}
                            options={overallStatusOptions}
                            value={tempFilters.status}
                            onChange={(value) => setTempFilters(prev => ({ ...prev, status: value }))}
                            forceHorizontal={true}
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <SingleSelect
                            label={String(t('tms.task.loadingDetailDrawer.destinationSite'))}
                            options={siteOptions}
                            value={tempFilters.site}
                            onChange={(value) => setTempFilters(prev => ({ ...prev, site: value }))}
                            forceHorizontal={true}
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-4">
                    <span className="mr-auto text-xs text-gray-500">
                        {String(t('tms.task.loadingDetailDrawer.dataRetentionHint'))}
                    </span>
                    <Button
                        type="primary"
                        onClick={handleApplyFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{String(t('components.button.filter'))}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{String(t('components.button.reset'))}</span>
                    </Button>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={paginatedData}
                loading={loading}
                rowKey="trackingNumber"
                scroll={{ x: 'max-content' }}
                pagination={false}
                size="middle"
                className="mb-4"
            />

            <TablePaginationBar
                total={currentQueryWaypointId === waypointId ? currentTotalElements : 0}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    handlePageChange(page, size);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* 订单详情抽屉 */}
            {selectedOrderId && (
                <ParcelDetailDrawer
                    visible={parcelDetailVisible}
                    onClose={() => setParcelDetailVisible(false)}
                    orderId={selectedOrderId}
                    userRole={userRole}
                    zIndex={1200}
                />
            )}
        </EditableDrawer>
    );
};

export default LoadingDetailDrawer;

